import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const API_BASE = `https://v3.football.api-sports.io`;

const palettes = {
    text: "#0f172a",
    muted: "#6b7280",
    border: "rgba(0,0,0,0.08)",
    soft: "#f7f9f7",
    brand: "#2563eb",
    brandSoft: "rgba(37,99,235,.10)",
    brandBorder: "rgba(37,99,235,.25)",
    green: "#16a34a",
};

const sectionStyle = { padding: "100px 20px", background: "#ffffff", position: "relative" };
const containerStyle = { maxWidth: 1160, margin: "0 auto" };
const headerStyle = { textAlign: "center", marginBottom: 20 };
const titleStyle = { margin: 0, fontSize: 28, fontWeight: 800, color: palettes.text };
const subtitleStyle = { margin: "8px auto 0", maxWidth: 760, color: palettes.muted };

const gridStyle = { display: "grid", gap: 16, gridTemplateColumns: "repeat(1, minmax(0, 1fr))" };
const gridLarge = { ...gridStyle, gridTemplateColumns: "repeat(3, 1fr)" };

const cardStyle = {
    position: "relative",
    background: "linear-gradient(180deg, rgba(255,255,255,.95), rgba(255,255,255,.9))",
    border: `1px solid ${palettes.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 24px rgba(0,0,0,.06)",
    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
};
const cardHover = { transform: "translateY(-3px)", boxShadow: "0 14px 32px rgba(0,0,0,.12)" };

const crest = { width: 40, height: 40, borderRadius: "50%", border: `1px solid ${palettes.border}`, objectFit: "contain", background: "#fff" };
const metaRow = { textAlign: "center", marginTop: 10, alignItems: "center", gap: 10, color: palettes.muted, fontSize: 13 };
const skeleton = { background: "linear-gradient(90deg, #f3f4f6, #eef2f7, #f3f4f6)", animation: "shimmer 1.2s infinite", borderRadius: 12 };
const skBox = (h = 20) => ({ ...skeleton, height: h });

const teamBlock = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
};
const teamName = {
    fontWeight: 700,
    color: palettes.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 150,
    fontSize: 14,
};

const midBlock = { display: "grid", placeItems: "center", textAlign: "center", minWidth: 0 };

const getGridStyle = () => {
    if (typeof window === "undefined") return gridStyle;
    if (window.innerWidth >= 1024) return gridLarge;
    if (window.innerWidth >= 640) return { ...gridStyle, gridTemplateColumns: "repeat(2, 1fr)" };
    return gridStyle;
};


export default function UpcomingFixtures({
    rapidApiKey = process.env.REACT_APP_RAPIDAPI_KEY,
    date = new Date().toISOString().slice(0, 10), 
    leagueIds, 
    limit = 9,
    title = "Upcoming Fixtures",
    subtitle = "Handpicked matches for today with league, time, and quick model context.",
    demoFallback,
}) {
    const [items, setItems] = React.useState(null);
    const [err, setErr] = React.useState(null);
    const [hoverIndex, setHoverIndex] = React.useState(-1);
    const [grid, setGrid] = React.useState(getGridStyle());
    const [tick, setTick] = React.useState(0);
    const [now, setNow] = React.useState(() => new Date());

    useEffect(() => {
        const onResize = () => setGrid(getGridStyle());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setNow(new Date());
            setTick((t) => t + 1);
        }, 15 * 60 * 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                if (!rapidApiKey && demoFallback) {
                    const list = (demoFallback || []).slice().sort((a, b) => new Date(a.time || a.date) - new Date(b.time || b.date));
                    setItems(list.slice(0, limit));
                    return;
                }
                const params = new URLSearchParams({ date });
                const url = `${API_BASE}/fixtures?${params.toString()}`;
                const res = await fetch(url, {
                    headers: {
                        "X-APISports-Key": rapidApiKey || "",
                    },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();

                let list = (json?.response || []).map((r) => ({
                    id: r.fixture?.id,
                    leagueId: r.league?.id,
                    leagueName: r.league?.name,
                    leagueLogo: r.league?.logo,
                    time: r.fixture?.date,
                    venue: r.fixture?.venue?.name,
                    home: { name: r.teams?.home?.name, logo: r.teams?.home?.logo },
                    away: { name: r.teams?.away?.name, logo: r.teams?.away?.logo },
                    status: r.fixture?.status?.short,
                }));

                if (leagueIds?.length) {
                    list = list.filter((r) => leagueIds.includes(r.leagueId));
                }

                const nowLocal = now;
                list = list.filter((r) => {
                    const ko = new Date(r.time);
                    const dStr = r.time?.slice(0, 10);
                    return dStr === date && ko.getTime() > nowLocal.getTime();
                });

                list.sort((a, b) => new Date(a.time) - new Date(b.time));
                list = list.slice(0, limit);

                if (!cancelled) setItems(list);
            } catch (e) {
                if (!cancelled) {
                    setErr(e.message || "Failed to load fixtures");
                    if (demoFallback) {
                        const list = (demoFallback || []).slice().sort((a, b) => new Date(a.time || a.date) - new Date(b.time || b.date));
                        setItems(list.slice(0, limit));
                    }
                }
            }
        }

        load();
        return () => { cancelled = true; };
    }, [rapidApiKey, date, limit, leagueIds, demoFallback, tick, now]);

    return (
        <section style={sectionStyle}>
            <div style={containerStyle}>
                <header style={headerStyle}>
                    <h2 style={titleStyle}>{title}</h2>
                    <p style={subtitleStyle}>{subtitle}</p>
                </header>

                {err && (
                    <div style={{ margin: "10px 0 18px", color: "#dc2626", textAlign: "center", fontWeight: 600 }}>
                        {err} {demoFallback ? "(showing demo data)" : ""}
                    </div>
                )}

                <div style={grid}>
                    {(items || Array.from({ length: 6 })).map((m, i) =>
                        !items ? (
                            <div key={i} style={{ ...cardStyle }}>
                                <div style={{ ...skBox(24), width: "60%" }} />
                                <div style={{ height: 10 }} />
                                <div style={{ ...skBox(18), width: "90%" }} />
                                <div style={{ height: 8 }} />
                                <div style={{ ...skBox(18), width: "70%" }} />
                            </div>
                        ) : (
                            <article
                                key={m.id || i}
                                style={{ ...cardStyle, ...(hoverIndex === i ? cardHover : null) }}
                                onMouseEnter={() => setHoverIndex(i)}
                                onMouseLeave={() => setHoverIndex(-1)}
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }}>
                                    <div style={{ ...teamBlock, justifyContent: "flex-start" }}>
                                        {m.home.logo && <img src={m.home.logo} alt={m.home.name || "Home"} style={crest} />}
                                        <span style={{ ...teamName, textAlign: "left" }}>{m.home.name || "Home"}</span>
                                    </div>

                                    <div style={midBlock}>
                                        <div style={{ fontSize: 11, textTransform: "uppercase", color: palettes.muted }}>
                                            {formatLeague(m.leagueName)}
                                        </div>
                                        <div style={{ fontWeight: 800, color: palettes.text }}>
                                            {formatKickoff(m.time)}
                                        </div>
                                    </div>

                                    <div style={{ ...teamBlock, justifyContent: "flex-end" }}>
                                        <span style={{ ...teamName, textAlign: "right" }}>{m.away.name || "Away"}</span>
                                        {m.away.logo && <img src={m.away.logo} alt={m.away.name || "Away"} style={crest} />}
                                    </div>
                                </div>

                                <div style={metaRow}>
                                    {m.venue && <span style={{ fontWeight: 600 }}>{m.venue}</span>}
                                </div>

                                <div style={metaRow}>
                                    <span style={{ fontSize: 12, marginLeft: "auto", color: palettes.muted, background: palettes.soft, border: `1px dashed ${palettes.border}`, padding: "4px 8px", borderRadius: 999 }}>
                                        {statusLabel(m.status)}
                                    </span>
                                </div>
                            </article>
                        )
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-12 text-center" style={{ marginTop: 40 }}>
                    <Link to="/fixtures" className="view-more wow fadeInUp animated">View all
                        <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <g clipPath="url(#clip0_6718_7111)">
                                <path d="M5.25 4.5L12.75 12L5.25 19.5" stroke="#121212" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12.75 4.5L20.25 12L12.75 19.5" stroke="#121212" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                        </svg>
                    </Link>
                </div>
            </div>

            <div
                aria-hidden
                style={{
                    position: "absolute",
                    inset: "auto 0 0 0",
                    height: 180,
                    background: "radial-gradient(600px 120px at 50% 0, rgba(37,99,235,.08), transparent 70%)",
                    pointerEvents: "none",
                }}
            />
        </section>
    );
}

function formatKickoff(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
}

function statusLabel(s) {
    if (!s) return "Scheduled";
    if (s === "NS") return "Not started";
    if (s === "FT") return "Full time";
    if (s === "HT") return "Half time";
    return s;
}

function formatLeague(name) {
    if (!name) return "League";
    return name.length > 24 ? name.slice(0, 24) + "…" : name;
}
